import {
  createRoute,
  Link,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import { useRef, useState, type FormEvent } from 'react';
import z from 'zod';

import ApiError from 'src/api/ApiError';
import useAuth from 'src/api/auth/useAuth';
import useEmailPasswordLogIn from 'src/api/auth/useEmailPasswordLogIn';
import { Button } from 'src/components/ui/button';
import config from 'src/config';
import { Route as rootRoute } from 'src/pages/__root';

import { MIN_AUTH_LOADING_MS } from './auth-constants';
import { AuthErrorMessage } from './AuthErrorMessage';
import { AuthServiceErrorState } from './AuthServiceErrorState';
import { AuthShell } from './AuthShell';
import { Spinner } from 'src/components/ui/spinner';
import { isSupportedConnectionType, SsoLoginButton } from './SsoLoginButton';
import { useWorkOSUrl } from './useWorkOSUrl';
import { useGoogleAuthUrl } from 'src/api/auth/useGoogleAuthUrl';

export const Route = createRoute({
  path: '/login',
  getParentRoute: () => rootRoute,
  validateSearch: z.object({
    redirect: z
      .string()
      .transform((href) => {
        if (!href) return undefined;

        const redirectHref =
          href.length > 2000 ? href.substring(0, href.indexOf('?')) : href;
        return redirectHref;
      })
      .optional(),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.currentUser) {
      throw redirect({ to: search.redirect || '/' });
    }
  },
  component: LoginPage,
});

export function LoginPage() {
  const gt = useGT();
  const navigate = useNavigate();
  const {
    isFetchingAuthInitialStatus,
    currentUser,
    isRefetching,
    refetchUser,
  } = useAuth();
  const { redirect } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [isShowingLoggingIn, setIsShowingLoggingIn] = useState(false);
  const loginStartRef = useRef(0);

  const {
    mutate: emailPasswordLogInApi,
    isLoading: isAuthenticating,
    error,
  } = useEmailPasswordLogIn(async () => {
    const remaining = loginStartRef.current + MIN_AUTH_LOADING_MS - Date.now();
    if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
    await refetchUser();
    navigate({ to: redirect || '/' });
  });

  const {
    data: workOSUrlData,
    isLoading: isLoadingWorkOSUrl,
    isRefetching: isRefetchingWorkOSUrl,
    error: workOSUrlError,
    refetch: refetchWorkOSUrl,
  } = useWorkOSUrl();

  const { data: googleAuthData, isLoading: isLoadingGoogleUrl } =
    useGoogleAuthUrl();
  const googleAuthorizationUrl = googleAuthData?.authorizationUrl ?? null;

  const isDisabled = isRefetching || isAuthenticating || isShowingLoggingIn;

  const allConnections = workOSUrlData?.connections ?? [];
  const supportedConnections = allConnections.filter((c) =>
    isSupportedConnectionType(c.connectionType),
  );
  const hasUnsupportedConnections = allConnections.some(
    (c) => !isSupportedConnectionType(c.connectionType),
  );
  const showFallback =
    supportedConnections.length === 0 || hasUnsupportedConnections;

  const ssoButtonList: React.ReactNode[] = [];

  // Native "Sign in with Google" (direct Google OAuth, independent of WorkOS).
  if (googleAuthorizationUrl != null) {
    ssoButtonList.push(
      <SsoLoginButton
        key="google"
        connectionType="GoogleOAuth"
        onClick={() => window.location.assign(googleAuthorizationUrl)}
        disabled={isDisabled}
        isLoading={isLoadingGoogleUrl}
      />,
    );
  }

  for (const conn of supportedConnections) {
    ssoButtonList.push(
      <SsoLoginButton
        key={conn.authorizationUrl}
        connectionType={conn.connectionType}
        onClick={() => window.location.assign(conn.authorizationUrl)}
        disabled={isDisabled}
        isLoading={isLoadingWorkOSUrl}
      />,
    );
  }

  const fallbackAuthorizationUrl = workOSUrlData?.fallbackAuthorizationUrl;
  if (showFallback && fallbackAuthorizationUrl != null) {
    ssoButtonList.push(
      <SsoLoginButton
        key="fallback"
        connectionType={null}
        onClick={() => window.location.assign(fallbackAuthorizationUrl)}
        disabled={isDisabled}
        isLoading={isLoadingWorkOSUrl}
      />,
    );
  }

  let ssoButtons: React.ReactNode = null;
  if (ssoButtonList.length > 0) {
    ssoButtons = ssoButtonList;
  }

  const showSsoSection = ssoButtons != null || Boolean(workOSUrlError);
  const isWorkOSBackendUnavailable =
    workOSUrlError instanceof ApiError && workOSUrlError.status === 503;

  function validateInputs() {
    const newErrors = { email: '', password: '' };
    let isValid = true;

    if (!email) {
      newErrors.email = gt('Email is required.', {
        context:
          'validation error shown when the email field is empty on the login form',
      });
      isValid = false;
    }

    if (!password) {
      newErrors.password = gt('Password is required.', {
        context:
          'validation error shown when the password field is empty on the login form',
      });
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }

  function handleLogin() {
    if (!validateInputs()) return;

    setIsShowingLoggingIn(true);
    loginStartRef.current = Date.now();
    emailPasswordLogInApi(
      { email, password },
      {
        onError: () => {
          const remaining =
            loginStartRef.current + MIN_AUTH_LOADING_MS - Date.now();
          if (remaining > 0) {
            setTimeout(() => setIsShowingLoggingIn(false), remaining);
          } else {
            setIsShowingLoggingIn(false);
          }
        },
      },
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    handleLogin();
  }

  // Still loading auth, or user is already logged in (redirect from beforeLoad is imminent)
  if (isFetchingAuthInitialStatus || currentUser) {
    return null;
  }

  if (isShowingLoggingIn) {
    return (
      <AuthShell
        washOut
        title={
          <T context="heading shown while a direct login is being completed">
            Logging in to <Var>{config.appName}</Var>
          </T>
        }
        description={
          <T context="description shown while a direct login is being completed">
            Preparing your workspace...
          </T>
        }
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <Spinner size="xl" />
          <p className="text-center text-gray-500 text-sm font-light leading-relaxed">
            <T context="status message shown while a direct login is being completed">
              Checking your session and loading your permissions.
            </T>
          </p>
        </div>
      </AuthShell>
    );
  }

  if (isWorkOSBackendUnavailable) {
    return (
      <AuthServiceErrorState
        error={workOSUrlError}
        isRetrying={isLoadingWorkOSUrl || isRefetchingWorkOSUrl}
        onRetry={() => {
          void refetchWorkOSUrl();
        }}
      />
    );
  }

  return (
    <AuthShell washOut={false}>
      <form noValidate onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="login-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {gt('Email', {
                context: 'label for email input field on login page',
              })}
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              disabled={isDisabled}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label
                htmlFor="login-password"
                className="block text-sm font-medium text-gray-700"
              >
                {gt('Password', {
                  context: 'label for password input field on login page',
                })}
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-gray-500 hover:text-[#1a1a1a]"
              >
                <T context="link from the login page to the forgot-password page">
                  Forgot password?
                </T>
              </Link>
            </div>
            <input
              id="login-password"
              type="password"
              value={password}
              disabled={isDisabled}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>
        </div>

        <Button type="submit" disabled={isDisabled} className="w-full mt-6">
          {isAuthenticating || isRefetching ? (
            <Spinner className="mx-auto text-white" />
          ) : (
            <T context="button label to submit login form">Log in</T>
          )}
        </Button>

        <AuthErrorMessage error={error} className="mt-4" />

        {showSsoSection && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                <T context="separator text between direct login and SSO on the login page">
                  or
                </T>
              </span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <div className="space-y-2">{ssoButtons}</div>

            <AuthErrorMessage error={workOSUrlError} />
          </div>
        )}
      </form>
    </AuthShell>
  );
}
